import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Trash2 } from "lucide-react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import api from "../../lib/api";
import { errorMessage, fmtDay, fromISODate, toISODate } from "../../lib/stays";

const REASONS = { MAINTENANCE: "Maintenance", OWNER_STAY: "Owner stay", RENOVATION: "Renovation", PRIVATE_EVENT: "Private event", OTHER: "Other" };
const LEGEND = [
  ["Available", "bg-[#12100f] border-white/10"],
  ["Booked", "bg-red-900/50 border-red-500/40"],
  ["Blocked", "bg-white/10 border-white/20"],
  ["Maintenance", "bg-amber-700/40 border-amber-500/40"],
  ["Check-in", "border-green-400/70"],
  ["Check-out", "border-sky-400/70"],
];

export default function AdminCalendar({ property }) {
  const [month, setMonth] = useState(() => { const d = new Date(); d.setDate(1); d.setHours(0, 0, 0, 0); return d; });
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [block, setBlock] = useState({ blocked_from: "", blocked_to: "", reason: "MAINTENANCE", note: "" });
  const [busy, setBusy] = useState(false);

  const range = useMemo(() => {
    const first = new Date(month); first.setDate(1 - ((first.getDay() + 6) % 7));       // Monday-start grid
    const last = new Date(first); last.setDate(last.getDate() + 41);
    return { first, last };
  }, [month]);

  const load = useCallback(async () => {
    if (!property) return;
    try {
      const { data: d } = await api.get("/admin/calendar", { params: { property: property.id, from: toISODate(range.first), to: toISODate(range.last) } });
      setData(d);
    } catch (e) { toast.error(errorMessage(e, "Couldn't load the calendar")); }
  }, [property, range]);
  useEffect(() => { load(); }, [load]);

  const cells = useMemo(() => {
    if (!data) return [];
    const byDay = {};
    data.days.forEach((d) => { byDay[d.date] = { ...d, bookings: [], checkIn: [], checkOut: [], block: null }; });
    data.bookings.forEach((b) => {
      const start = fromISODate(b.check_in_date), end = fromISODate(b.check_out_date);
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const c = byDay[toISODate(d)]; if (!c) continue;
        const iso = toISODate(d);
        if (b.booking_type === "overnight" ? iso < b.check_out_date : true) c.bookings.push(b);
        if (iso === b.check_in_date) c.checkIn.push(b);
        if (iso === b.check_out_date && b.booking_type === "overnight") c.checkOut.push(b);
      }
    });
    data.blocks.forEach((bl) => {
      for (let d = fromISODate(bl.blocked_from); d <= fromISODate(bl.blocked_to); d.setDate(d.getDate() + 1)) {
        const c = byDay[toISODate(d)]; if (c) c.block = bl;
      }
    });
    return data.days.map((d) => byDay[d.date]);
  }, [data]);

  const cls = (c) => {
    if (c.block) return c.block.reason === "MAINTENANCE" ? "bg-amber-700/40 border-amber-500/40" : "bg-white/10 border-white/20";
    if (c.bookings.some((b) => b.booking_type === "overnight")) return "bg-red-900/50 border-red-500/40";
    if (c.bookings.length) return "bg-red-900/25 border-red-500/25";
    return "bg-[#12100f] border-white/10";
  };

  const addBlock = async () => {
    if (!block.blocked_from) { toast.error("Choose the first date to block"); return; }
    setBusy(true);
    try {
      await api.post("/admin/blocks", { ...block, blocked_to: block.blocked_to || block.blocked_from, property: property.id });
      toast.success("Dates blocked");
      setBlock({ blocked_from: "", blocked_to: "", reason: "MAINTENANCE", note: "" });
      load();
    } catch (e) { toast.error(errorMessage(e)); }
    setBusy(false);
  };
  const removeBlock = async (id) => {
    if (!window.confirm("Unblock these dates?")) return;
    try { await api.delete(`/admin/blocks/${id}`); toast.success("Unblocked"); load(); } catch (e) { toast.error(errorMessage(e)); }
  };

  const sel = selected && cells.find((c) => c.date === selected);
  const shift = (n) => setMonth(new Date(month.getFullYear(), month.getMonth() + n, 1));

  return (
    <div className="grid lg:grid-cols-[1fr_320px] gap-6" data-testid="admin-calendar">
      <div className="glass p-5">
        <div className="flex items-center justify-between mb-4">
          <button onClick={() => shift(-1)} aria-label="Previous month"><ChevronLeft /></button>
          <h3 className="font-display text-xl">{month.toLocaleDateString("en-IN", { month: "long", year: "numeric" })}</h3>
          <button onClick={() => shift(1)} aria-label="Next month"><ChevronRight /></button>
        </div>
        <div className="grid grid-cols-7 gap-1 text-center text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1">{["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <div key={d}>{d}</div>)}</div>
        <div className="grid grid-cols-7 gap-1" role="grid" aria-label="Availability calendar">
          {cells.map((c) => {
            const d = fromISODate(c.date);
            const other = d.getMonth() !== month.getMonth();
            return (
              <button key={c.date} onClick={() => setSelected(c.date)} role="gridcell" aria-label={`${fmtDay(c.date)}: ${c.block ? "blocked" : c.bookings.length ? "booked" : "available"}`}
                className={`relative h-14 sm:h-16 border text-left p-1.5 text-xs transition-colors hover:border-gold ${cls(c)} ${other ? "opacity-40" : ""} ${selected === c.date ? "ring-1 ring-gold" : ""}`}>
                <span className="text-white/80">{d.getDate()}</span>
                {c.checkIn.length > 0 && <i className="absolute left-1 bottom-1 w-2 h-2 rounded-full bg-green-400" title="Check-in" />}
                {c.checkOut.length > 0 && <i className="absolute right-1 bottom-1 w-2 h-2 rounded-full bg-sky-400" title="Check-out" />}
                {c.bookings[0] && <span className="block truncate text-[10px] text-white/60 mt-0.5">{c.bookings[0].guest_name?.split(" ")[0]}</span>}
                {c.block && <span className="block truncate text-[10px] text-amber-200/80 mt-0.5">{REASONS[c.block.reason]}</span>}
              </button>
            );
          })}
        </div>
        <div className="flex flex-wrap gap-4 mt-4 text-[11px] text-white/50">{LEGEND.map(([t, c]) => <span key={t} className="flex items-center gap-2"><i className={`w-3 h-3 border inline-block ${c}`} /> {t}</span>)}</div>

        {sel && (
          <div className="mt-5 border-t border-white/10 pt-4 text-sm" data-testid="calendar-day-detail">
            <div className="font-display text-lg">{fmtDay(sel.date)}</div>
            {sel.block && <div className="text-amber-200/90 mt-2">Blocked: {REASONS[sel.block.reason]} {sel.block.note && `— ${sel.block.note}`}</div>}
            {sel.bookings.length === 0 && !sel.block && <div className="text-white/40 mt-2">Available</div>}
            {sel.bookings.map((b) => <div key={b.id} className="text-white/70 mt-2">{b.reference} · {b.guest_name} · {b.status.replace("_", " ")} {b.check_in_date === sel.date && "· check-in"}</div>)}
            {sel.checkOut.map((b) => <div key={`o${b.id}`} className="text-sky-300/80 mt-2">{b.reference} checks out today</div>)}
          </div>
        )}
      </div>

      <div className="space-y-4">
        <div className="glass p-5">
          <div className="eyebrow mb-3">Block dates</div>
          <div className="space-y-3">
            <div><Label className="text-xs text-white/50">From</Label><Input type="date" value={block.blocked_from} onChange={(e) => setBlock({ ...block, blocked_from: e.target.value })} className="mt-1 bg-[#0f0f0f] border-white/10 h-10" data-testid="block-from" /></div>
            <div><Label className="text-xs text-white/50">To (inclusive)</Label><Input type="date" value={block.blocked_to} onChange={(e) => setBlock({ ...block, blocked_to: e.target.value })} className="mt-1 bg-[#0f0f0f] border-white/10 h-10" data-testid="block-to" /></div>
            <div><Label className="text-xs text-white/50">Reason</Label>
              <select value={block.reason} onChange={(e) => setBlock({ ...block, reason: e.target.value })} className="mt-1 w-full bg-[#0f0f0f] border border-white/10 h-10 px-2 text-sm" data-testid="block-reason-select">
                {Object.entries(REASONS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
            <Input placeholder="Note (optional)" value={block.note} onChange={(e) => setBlock({ ...block, note: e.target.value })} className="bg-[#0f0f0f] border-white/10 h-10" />
            <button disabled={busy} onClick={addBlock} className="btn-gold w-full disabled:opacity-40" data-testid="block-submit">Block dates</button>
          </div>
        </div>
        <div className="glass p-5">
          <div className="eyebrow mb-3">Blocks in view</div>
          {(data?.blocks || []).length === 0 ? <div className="text-sm text-white/40">None</div> : data.blocks.map((b) => (
            <div key={b.id} className="flex items-center justify-between gap-2 py-2 border-b border-white/5 text-sm">
              <div><div className="text-white/80">{b.blocked_from}{b.blocked_to !== b.blocked_from && ` → ${b.blocked_to}`}</div><div className="text-xs text-white/40">{REASONS[b.reason]}{b.created_by && ` · ${b.created_by}`}</div></div>
              <button onClick={() => removeBlock(b.id)} className="text-red-400" aria-label="Remove block"><Trash2 size={14} /></button>
            </div>))}
        </div>
      </div>
    </div>
  );
}
